import { compact, flow, isEqual, isNull, map, reject, sum } from 'lodash/fp';
import { createSelector } from 'reselect';
import { Hidden, Price } from '@ahmdigital/ui';
import { PRODUCT_TYPE } from '@ahmdigital/constants';
import { useSelector } from 'react-redux';
import moment from 'moment';

import { PremiumReviewText } from '../../styles';
import { useByocNavigationContext } from '../../../../context/byoc-navigation-context';
import getFutureDate from '../../../../../../utils/get-future-date';
import isFeatureEnabled from '../../../../../../utils/is-feature-enabled';
import PREMIUM_CHANGE from '../../../../../../features/premium-change/constants';
import PriceService, { Price as PriceData } from '../../../../../../models/price';
import selectBuildYourCover from '../../../../../../selectors/select-build-your-cover';
import selectCustomer from '../../../../../../selectors/select-customer';
import selectEffectiveDateOverride from '../../../../../../selectors/select-effective-date-override';
import selectPrices from '../../../../../../selectors/select-prices';
import selectRawPrice from '../../../../../../selectors/select-raw-price';
import truncateFrequency from '../../../../../../utils/truncate-frequency';

const selectStateForComponent = (journeyProductType: string) =>
  createSelector(
    [selectCustomer, selectEffectiveDateOverride, selectPrices, selectBuildYourCover],
    (customer, effectiveDateOverride, prices, { selectedProducts }) => {
      let keyToReject: string | undefined;
      if (journeyProductType === PRODUCT_TYPE.HOSPITAL) {
        keyToReject = PRODUCT_TYPE.EXTRAS;
      } else if (journeyProductType === PRODUCT_TYPE.EXTRAS) {
        keyToReject = PRODUCT_TYPE.HOSPITAL;
      }
      const selectedProductIds =
        journeyProductType !== PRODUCT_TYPE.PACKAGE
          ? (reject(
              isNull,
              Object.entries(selectedProducts)
                .filter(([key]) => key !== keyToReject)
                .map(([, value]) => value),
            ) as string[])
          : (reject(isNull, selectedProducts) as string[]);

      const date = getFutureDate(Boolean(effectiveDateOverride));
      const allPrices = compact(PriceService.getPrices(prices, selectedProductIds, customer, { date }));

      return {
        frequency: customer.frequency,
        isLoading: allPrices.length !== selectedProductIds.length,
        selectedTotalProductPrice:
          allPrices.length > 0
            ? flow(
                // @ts-expect-error [🤖 PLEASE FIX 🤖]: Argument of type 'string | undefined' is not assignable to parameter of type 'string'.
                map((priceData: PriceData) => parseFloat(priceData.amount?.[customer.frequency])),
                sum,
              )(allPrices)
            : null,
      };
    },
  );

const ProductPriceTotal = () => {
  const { journeyType } = useByocNavigationContext();
  const { frequency, isLoading, selectedTotalProductPrice } = useSelector(
    selectStateForComponent(journeyType),
    isEqual,
  );

  const { useFutureEffectiveDate } = useSelector(selectRawPrice);

  const isPremiumChangeEnabled = isFeatureEnabled('premiumChange');
  const formattedEffectiveDate = moment(PREMIUM_CHANGE.FUTURE_EFFECTIVE_DATE).format('D MMMM');

  if (selectedTotalProductPrice) {
    const totalPriceForPriceComponent = PriceService.getPriceAsObject(selectedTotalProductPrice);

    return (
      <>
        <Hidden xs sm md>
          <Price
            frequency={truncateFrequency(frequency)}
            price={totalPriceForPriceComponent}
            showAsterisk
            showFrom
            isFrequencyInline
          />
        </Hidden>
        <Hidden lg df>
          <Price frequency={truncateFrequency(frequency)} price={totalPriceForPriceComponent} showAsterisk showFrom />
        </Hidden>
        {isPremiumChangeEnabled && (
          <PremiumReviewText>
            {useFutureEffectiveDate ? 'After' : 'Before'} {formattedEffectiveDate}
          </PremiumReviewText>
        )}
      </>
    );
  }

  return (
    // @ts-expect-error [🤖 PLEASE FIX 🤖]: Type '{ cents: string; dollars: string; } | null' is not assignable to type '{ cents?: string | undefined; dollars?: string | undefined; } | undefined'.
    <Price frequency={frequency} price={isLoading ? null : { cents: '--', dollars: '--' }} showAsterisk showFrom />
  );
};

export default ProductPriceTotal;
